// ─── Connection status ────────────────────────────────────────────────────────
export type WebSocketStatus =
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'error'

// ─── Generic server-frame shape ───────────────────────────────────────────────
/**
 * Every message from the server has this top-level structure.
 * `T` narrows the `data` field for specific event types.
 */
export interface WebSocketMessage<T = unknown> {
  type: string
  data: T
  timestamp: string
}

// ─── Domain event payloads ────────────────────────────────────────────────────
export type OrderCreatedPayload = {
  id: number
  order_number: string
  status: string
  total_price: string
  payment_method: string
  delivery_method: string
  user: {
    first_name: string
    username: string // phone number
  }
  target_admin_ids: number[]
}

// ─── Typed event frames ───────────────────────────────────────────────────────
/**
 * Utility for building strongly-typed event frames.
 * Extend AppWebSocketEvent union below when new event types arrive.
 */
export type WebSocketEvent<T extends string, D> = {
  type: T
  data: D
  timestamp: string
}

export type OrderCreatedEvent = WebSocketEvent<
  'order.created',
  OrderCreatedPayload
>

export type NotificationReadPayload = {
  notification_id: number
  warehouse_id: number
  object_id: number
  notification_type: string
  read_by_user_id: number
  target_admin_ids: number[]
}

export type NotificationReadEvent = WebSocketEvent<
  'notification.read',
  NotificationReadPayload
>

export type TransferEventPayload = {
  id: number
  user: {
    first_name: string
    username: string
  }
  status: string
  from_warehouse: string
  to_warehouse: string
  total_price: string
  target_admin_ids: number[]
  comment: string | null
}

export type TransferSentEvent = WebSocketEvent<
  'transfer.sent',
  TransferEventPayload
>
export type TransferReturnedEvent = WebSocketEvent<
  'transfer.returned',
  TransferEventPayload
>
export type TransferCreatedFromOrderEvent = WebSocketEvent<
  'transfer.created_from_order',
  TransferEventPayload
>
export type TransferRespondedEvent = WebSocketEvent<
  'transfer.responded',
  TransferEventPayload
>
export type TransferRequestedEvent = WebSocketEvent<
  'transfer.requested',
  TransferEventPayload
>
export type TransferApprovedEvent = WebSocketEvent<
  'transfer.approved',
  TransferEventPayload
>
export type TransferCancelledEvent = WebSocketEvent<
  'transfer.cancelled',
  TransferEventPayload
>

// ─── Sales window event payloads ──────────────────────────────────────────────
export type SalesWindowNotificationPayload = {
  id: number
  status: string
  send_type: string
  warehouse_id: number
  warehouse: string
  customer: {
    first_name: string
    username: string
  }
  total_price: string
  notification_type: string
  deadline: string | null
  target_admin_ids: number[]
}

export type SalesWindowAutoReturnWarningEvent = WebSocketEvent<
  'sales_window.auto_return_warning',
  SalesWindowNotificationPayload
>
export type SalesWindowAutoReturnedEvent = WebSocketEvent<
  'sales_window.auto_returned',
  SalesWindowNotificationPayload
>
export type SalesWindowAutoCancelWarningEvent = WebSocketEvent<
  'sales_window.auto_cancel_warning',
  SalesWindowNotificationPayload
>
export type SalesWindowAutoCancelledEvent = WebSocketEvent<
  'sales_window.auto_cancelled',
  SalesWindowNotificationPayload
>

export type SalesWindowNotificationEvent =
  | SalesWindowAutoReturnWarningEvent
  | SalesWindowAutoReturnedEvent
  | SalesWindowAutoCancelWarningEvent
  | SalesWindowAutoCancelledEvent

// ─── Warehouse debt limit event payloads ──────────────────────────────────────
export type WarehouseDebtLimitConsumedPayload = {
  order_id: number
  warehouse_id: number
  warehouse_name: string
  debt_limit: string
  current_debt: string
  remaining_limit: string
  remaining_limit_display: string
  notification_type: 'warehouse_debt_limit_consumed'
  target_admin_ids: number[]
}

export type WarehouseDebtLimitConsumedEvent = WebSocketEvent<
  'warehouse.debt_limit_consumed',
  WarehouseDebtLimitConsumedPayload
>

// ─── Chat event payloads ──────────────────────────────────────────────────────
export type ChatSessionCreatedPayload = {
  session_id: string
  user: { id: number; name: string; phone: string }
  category: { id: number; name: string }
  created_at: string
}

export type ChatMessageCreatedPayload = {
  session_id: string
  message: {
    id: string
    sender: { id: number; name: string; role: 'customer' | 'worker' }
    content: string
    file: string | null
    message_type: 'text' | 'image' | 'file'
    created_at: string
  }
}

export type ChatSessionClosedPayload = {
  session_id: string
  closed_at: string
}

export type ChatMessageReadPayload = {
  session_id: string
  message_id: string
  is_read: boolean
  read_at: string
}

export type ChatSessionMessagesReadPayload = {
  session_id: string
  message_ids: string[]
  read_at: string
}

// ─── Chat event envelopes ─────────────────────────────────────────────────────
export type ChatSessionCreatedEvent = {
  type: 'chat.session.created'
  data: ChatSessionCreatedPayload
}

export type ChatMessageCreatedEvent = {
  type: 'chat.message.created'
  data: ChatMessageCreatedPayload
}

export type ChatSessionClosedEvent = {
  type: 'chat.session.closed'
  data: ChatSessionClosedPayload
}

export type ChatMessageReadEvent = WebSocketEvent<
  'chat.message.read',
  ChatMessageReadPayload
>

export type ChatSessionMessagesReadEvent = WebSocketEvent<
  'chat.session.messages.read',
  ChatSessionMessagesReadPayload
>

// ─── App-wide union ───────────────────────────────────────────────────────────
export type AppWebSocketEvent =
  | OrderCreatedEvent
  | NotificationReadEvent
  | TransferSentEvent
  | TransferReturnedEvent
  | TransferCreatedFromOrderEvent
  | TransferRespondedEvent
  | TransferRequestedEvent
  | TransferApprovedEvent
  | TransferCancelledEvent
  | SalesWindowAutoReturnWarningEvent
  | SalesWindowAutoReturnedEvent
  | SalesWindowAutoCancelWarningEvent
  | SalesWindowAutoCancelledEvent
  | WarehouseDebtLimitConsumedEvent
  | ChatSessionCreatedEvent
  | ChatMessageCreatedEvent
  | ChatSessionClosedEvent
  | ChatMessageReadEvent
  | ChatSessionMessagesReadEvent

// Union of all possible incoming WS frames
export type AnyWebSocketMessage = AppWebSocketEvent | WebSocketMessage

// ─── Type guards ──────────────────────────────────────────────────────────────
export function isOrderCreatedEvent(
  event: AnyWebSocketMessage
): event is OrderCreatedEvent {
  return 'type' in event && event.type === 'order.created'
}

export function isNotificationReadEvent(
  event: AnyWebSocketMessage
): event is NotificationReadEvent {
  return 'type' in event && event.type === 'notification.read'
}

export function isTransferSentEvent(
  event: AnyWebSocketMessage
): event is TransferSentEvent {
  return 'type' in event && event.type === 'transfer.sent'
}

export function isTransferReturnedEvent(
  event: AnyWebSocketMessage
): event is TransferReturnedEvent {
  return 'type' in event && event.type === 'transfer.returned'
}

export function isTransferCreatedFromOrderEvent(
  event: AnyWebSocketMessage
): event is TransferCreatedFromOrderEvent {
  return 'type' in event && event.type === 'transfer.created_from_order'
}

export function isTransferRespondedEvent(
  event: AnyWebSocketMessage
): event is TransferRespondedEvent {
  return 'type' in event && event.type === 'transfer.responded'
}

export function isTransferRequestedEvent(
  event: AnyWebSocketMessage
): event is TransferRequestedEvent {
  return 'type' in event && event.type === 'transfer.requested'
}

export function isTransferApprovedEvent(
  event: AnyWebSocketMessage
): event is TransferApprovedEvent {
  return 'type' in event && event.type === 'transfer.approved'
}

export function isTransferCancelledEvent(
  event: AnyWebSocketMessage
): event is TransferCancelledEvent {
  return 'type' in event && event.type === 'transfer.cancelled'
}

const SALES_WINDOW_EVENT_TYPES = [
  'sales_window.auto_return_warning',
  'sales_window.auto_returned',
  'sales_window.auto_cancel_warning',
  'sales_window.auto_cancelled',
] as const

export function isSalesWindowNotificationEvent(
  event: AnyWebSocketMessage
): event is SalesWindowNotificationEvent {
  return (
    'type' in event &&
    (SALES_WINDOW_EVENT_TYPES as readonly string[]).includes(event.type)
  )
}

export function isWarehouseDebtLimitConsumedEvent(
  event: AnyWebSocketMessage
): event is WarehouseDebtLimitConsumedEvent {
  return 'type' in event && event.type === 'warehouse.debt_limit_consumed'
}

export function isChatSessionCreatedEvent(
  event: AnyWebSocketMessage
): event is ChatSessionCreatedEvent {
  return 'type' in event && event.type === 'chat.session.created'
}

export function isChatMessageCreatedEvent(
  event: AnyWebSocketMessage
): event is ChatMessageCreatedEvent {
  return 'type' in event && event.type === 'chat.message.created'
}

export function isChatSessionClosedEvent(
  event: AnyWebSocketMessage
): event is ChatSessionClosedEvent {
  return 'type' in event && event.type === 'chat.session.closed'
}

export function isChatMessageReadEvent(
  event: AnyWebSocketMessage
): event is ChatMessageReadEvent {
  return 'type' in event && event.type === 'chat.message.read'
}

export function isChatSessionMessagesReadEvent(
  event: AnyWebSocketMessage
): event is ChatSessionMessagesReadEvent {
  return 'type' in event && event.type === 'chat.session.messages.read'
}
